import { useCallback, useEffect, useRef, useState } from 'react';
import { MENU_TABS, menuItems, type Category, type MenuItem } from '@tikkas/shared';
import { ART, IC, Svg, imageFor } from '../art/icons.js';
import { builderFromConfig, newBuilder } from '../lib/bag.js';
import { track } from '../lib/events.js';
import { fmt, hhmmLocal, readyWhen } from '../lib/format.js';
import { collectLine, groupApi, membership, shareLink, type GroupLine, type GroupView } from '../lib/group.js';
import { qrSvg } from '../lib/qr.js';
import { useNav } from '../lib/router.js';
import { orderingOpen, useStore } from '../lib/store.js';
import { OrderingClosed, Scroll, TopBar } from '../components/ui.js';
import './group.css';

/*
 * The group order link, /g/<token> (docs/28): what everyone opens. A friend joins with a first name (or is known when
 * signed in), adds their own items with the normal builder (Build.tsx in group mode) and can change or remove them until
 * the host locks. The host sees every line arrive (a poll every few seconds), shares the link and QR, takes lines off,
 * locks or unlocks, and pays: the group's lines go in the bag and the normal checkout takes it from there. Once paid, the
 * page shows the order's progress to everyone who has the link.
 */

const initials = (name: string) => name.split(/\s+/).map((x) => x[0] ?? '').join('').slice(0, 2).toUpperCase();
const STATE_LABEL: Record<GroupView['state'], string> = { open: 'Open for adding', expired: 'Closed for adding', locked: 'Locked by the host', paying: 'Being paid for', paid: 'Paid', cancelled: 'Cancelled' };
const ORDER_LABEL: Record<string, string> = { placed: 'Order received', preparing: 'On the grill', ready: 'Ready to collect', collected: 'Collected', cancelled: 'Cancelled', refunded: 'Refunded', pending_payment: 'Awaiting payment' };

export function GroupLink() {
  const s = useStore();
  const { route, navigate } = useNav();
  const token = (route.params.id ?? '').toUpperCase();
  const menu = s.menuData?.menu;
  const [g, setG] = useState<GroupView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState(() => membership(token)?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [cat, setCat] = useState<Category>('bowls');
  const [shared, setShared] = useState<string | null>(null);
  const latest = useRef<GroupView | null>(null);
  latest.current = g;

  const load = useCallback(async () => {
    try {
      const v = await groupApi.get(token);
      setG(v);
      setErr(null);
    } catch (e) {
      if (!latest.current) setErr(e instanceof Error ? e.message : "This link isn't valid");
    }
  }, [token]);
  useEffect(() => {
    load();
  }, [load, s.me?.customer.id]);
  // Lines arrive live: every few seconds while the group is in play, slower once it is paid (the order's progress), never in the background.
  useEffect(() => {
    if (!g || g.state === 'cancelled' || g.order?.status === 'collected') return;
    const ms = g.state === 'paid' ? 10_000 : 4000;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, ms);
    return () => clearInterval(t);
  }, [g?.state, g?.order?.status, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn: () => Promise<GroupView>, done?: string) => {
    if (busy) return;
    setBusy(true);
    try {
      setG(await fn());
      if (done) s.toast(done);
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Something went wrong');
      load();
    } finally {
      setBusy(false);
    }
  };
  const join = (e: React.FormEvent) => {
    e.preventDefault();
    run(async () => {
      const v = await groupApi.join(token, name);
      track('group_joined', { groupId: v.id, signedIn: !!s.me });
      return v;
    });
  };
  const openBuilder = (it: MenuItem) => {
    s.setBuilder({ ...newBuilder(it.id), group: { token } });
    navigate('/build');
  };
  const editLine = (l: GroupLine) => {
    s.setBuilder({ ...builderFromConfig(l.config, l.id, l.qty), group: { token } });
    navigate('/build');
  };
  const share = async () => {
    if (!g) return;
    const r = await shareLink({ url: g.link, title: `${g.hostName}'s Tikkas order`, text: `Add what you want to ${g.hostName}'s Tikkas order, ${collectLine(g, readyWhen).toLowerCase()}. One pickup, your name on the bag.` });
    setShared(r === 'copied' ? 'Link copied' : r === 'failed' ? g.link : null);
    if (r === 'copied') s.toast('Link copied');
  };
  /** The host pays: lock, copy the group's lines into the bag as they are, and go to the normal checkout. */
  const pay = async () => {
    if (!g || busy) return;
    setBusy(true);
    try {
      const v = g.state === 'open' ? await groupApi.act(token, 'lock') : g;
      s.clearBag();
      s.setBag(v.lines.map((l) => ({ id: l.id, config: l.config, qty: l.qty })));
      s.setPickup(v.pickup);
      s.setGroupCheckout({ id: v.id, token: v.token, hostName: v.hostName });
      navigate('/bag');
    } catch (e) {
      s.toast(e instanceof Error ? e.message : 'Could not lock the group');
      setBusy(false);
    }
  };

  if (err && !g) return <Scroll padStyle={{ gap: 16, paddingTop: 16 }}><TopBar title="Group order" /><div className="card col" style={{ padding: 18, gap: 8 }}><b style={{ fontSize: 16 }}>This link isn't valid</b><p className="muted" style={{ margin: 0, fontSize: 14 }}>{err}</p></div><button type="button" className="btn2" onClick={() => navigate('/', { root: true })}>Go to Tikkas</button></Scroll>;
  if (!g || !menu) return <Scroll padStyle={{ gap: 16, paddingTop: 16 }}><TopBar title="Group order" /><p className="muted">Loading…</p></Scroll>;

  const me = g.me, isHost = !!me?.isHost;
  const live = g.state === 'open';
  const mine = g.lines.filter((l) => l.mine), others = g.lines.filter((l) => !l.mine);
  const items = g.lines.reduce((a, l) => a + l.qty, 0);
  const people = g.people.filter((p) => p.items > 0 || p.isHost).length;
  const unavailable = g.lines.some((l) => l.linePence === null);
  const head = isHost ? 'Your group order' : `${g.hostName}'s Tikkas order`;
  const closesAt = hhmmLocal(g.expiresAt);
  const lineRow = (l: GroupLine) => (
    <div className={`gline ${l.mine ? 'mine' : ''}`} key={l.id}>
      <span className="who" aria-hidden="true">{initials(l.name)}</span>
      <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
        <b>{l.qty > 1 ? `${l.qty} × ` : ''}{l.title}</b>
        {l.description ? <span className="small muted">{l.description}</span> : null}
        <span className="small muted">{l.mine ? 'Yours' : l.name}{l.linePence === null ? ' · not available today' : ''}</span>
      </span>
      {l.linePence !== null ? <span className="price">{fmt(l.linePence)}</span> : <span className="price muted">—</span>}
      {(l.mine && live) || (isHost && g.payable) ? (
        <span className="acts">
          {l.mine && live && <button type="button" className="pillbtn" onClick={() => editLine(l)}>Edit</button>}
          <button type="button" className="pillbtn" aria-label={`Remove ${l.title}`} onClick={() => run(() => groupApi.removeLine(token, l.id), 'Removed')}><Svg html={IC.bin} /></button>
        </span>
      ) : null}
    </div>
  );
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Group order" onBack={() => (isHost ? navigate('/group', { replace: true }) : navigate('/', { root: true }))} />
      <OrderingClosed compact />
      <div className="col" style={{ gap: 6 }}>
        <h1 className="display" style={{ fontSize: 30, lineHeight: 1.08, margin: 0 }}>{head}</h1>
        <span style={{ fontSize: 15 }}><b>{collectLine(g, readyWhen)}</b> · {s.config?.site.address ?? '1 Great Titchfield St'}</span>
        <span className={`small muted gstate ${live ? '' : 'off'}`}><i />{g.order ? `${ORDER_LABEL[g.order.status] ?? g.order.status} · order #${g.order.number}` : live ? `${STATE_LABEL.open} until ${closesAt}` : STATE_LABEL[g.state]}</span>
      </div>
      <div className="gpeople" aria-label="Who's in">{g.people.map((p) => <span className={`gperson ${p.isHost ? 'host' : ''}`} key={p.id}><span className="avatar">{initials(p.name)}</span>{p.mine ? 'You' : p.name}{p.items ? <span className="n">· {p.items}</span> : null}</span>)}</div>

      {g.order && (
        <div className="card col" style={{ padding: 18, gap: 8 }}>
          <b style={{ fontSize: 17 }}>{ORDER_LABEL[g.order.status] ?? g.order.status}</b>
          <span className="small muted">{g.order.status === 'collected' ? `Collected at ${g.order.collectedAt ? hhmmLocal(g.order.collectedAt) : ''}. Enjoy.` : `${collectLine({ readyAt: g.order.readyAt, scheduled: g.scheduled }, readyWhen)}. ${isHost ? 'Show your code at the counter and collect everything.' : `${g.hostName} shows the code at the counter and collects everything. Your name is on your bag.`}`}</span>
          {isHost && <button type="button" className="btn2" style={{ minHeight: 44 }} onClick={() => navigate(`/order/${g.order!.id}`)}>Open the order</button>}
          {!isHost && s.me && <button type="button" className="link inline" style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/account/orders')}>It's in your order history too</button>}
        </div>
      )}

      {!me && !g.order && (
        <form className="card col" style={{ padding: 18, gap: 12 }} onSubmit={join}>
          <b style={{ fontSize: 16 }}>{live ? `Add your own lunch to ${g.hostName}'s order` : 'This group order has closed'}</b>
          {live ? (
            <>
              <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>Pick what you want; {g.hostName} pays and collects, one pickup, your name on the bag.{s.me ? '' : ' No account needed.'}</p>
              {s.me ? <span className="small">Joining as <b>{s.me.customer.firstName || 'you'}</b></span> : <label className="cfield">Your first name (it goes on your bag)<input className="txt-in" value={name} maxLength={24} required autoComplete="given-name" onChange={(e) => setName(e.target.value)} /></label>}
              <button type="submit" className="yellow" disabled={busy || (!s.me && !name.trim())}>{busy ? 'One moment…' : 'Join and pick my food'}</button>
              {!s.me && <button type="button" className="link inline" style={{ alignSelf: 'center' }} onClick={() => navigate(`/join?next=${encodeURIComponent(`/g/${token}`)}`)}>Got a Tikkas account? Sign in to earn points on what you add</button>}
            </>
          ) : <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{g.state === 'cancelled' ? `${g.hostName} cancelled it.` : `Ask ${g.hostName} if you need something added.`}</p>}
        </form>
      )}

      {me && !g.order && g.state !== 'cancelled' && (
        <>
          <section className="col" style={{ gap: 10 }}>
            <div className="sechead"><h2 className="h3">{items ? `${items} item${items === 1 ? '' : 's'} from ${people} ${people === 1 ? 'person' : 'people'}` : 'Nothing added yet'}</h2>{items ? <span className="small muted tnum">{fmt(g.totalPence)}</span> : null}</div>
            {g.lines.length ? <div className="card" style={{ padding: 0, overflow: 'hidden' }}>{[...mine, ...others].map(lineRow)}</div> : <div className="cnote">{live ? (isHost ? 'Share the link and the items appear here as your friends add them.' : 'Nobody has added anything yet. You go first.') : 'Nothing was added.'}</div>}
            {live && orderingOpen(s) && !adding && <button type="button" className="btn2" style={{ minHeight: 50 }} onClick={() => setAdding(true)}><span style={{ fontSize: 20, lineHeight: 1 }}>+</span> Add an item{mine.length ? '' : ' for yourself'}</button>}
          </section>
          {live && adding && (
            <section className="col gmenu" style={{ gap: 10 }}>
              <div className="sechead"><h2 className="h3">Pick something</h2><button type="button" className="link" onClick={() => setAdding(false)}>Done</button></div>
              <div className="pills" role="tablist" aria-label="Menu sections">{MENU_TABS.map(([k, l]) => <button type="button" role="tab" className="pill" key={k} aria-selected={cat === k} onClick={() => setCat(k)}>{l}</button>)}</div>
              <div className="card rows">
                {menuItems(menu, cat).map((it) => (
                  <button type="button" className={`r ${it.soldOut ? 'soldrow' : ''}`} key={it.id} disabled={!!it.soldOut} onClick={() => openBuilder(it)}>
                    {it.image || it.photoUrl ? <img className="pic" src={imageFor(it.image, it.photoUrl)} alt="" loading="lazy" /> : <span className="pic"><Svg html={ART[it.id] ?? ART.tots!} /></span>}
                    <span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{it.name}</b><span className="small muted one">{it.soldOut ? 'Sold out today' : it.description}</span></span>
                    <span className="small" style={{ fontWeight: 700, flexShrink: 0 }}>{it.sizes.length > 1 ? 'from ' : ''}{fmt(it.sizes[0]!.pricePence)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
          {isHost ? (
            <>
              <section className="col" style={{ gap: 10 }}><h2 className="h3">Invite your friends</h2>
                <div className="codecard"><span className="grow col" style={{ gap: 2, minWidth: 0 }}><span className="small muted">Send them this link</span><span className="code" style={{ fontSize: 16, wordBreak: 'break-all' }}>{g.link.replace(/^https?:\/\//, '')}</span><span className="small muted">{live ? `Open for adding until ${closesAt}. It opens in the Tikkas app, or on the website for anyone without it.` : 'Unlock the group to let people add more.'}</span></span><button type="button" className="copy" onClick={share}>Share</button></div>
                {shared && shared !== 'Link copied' ? <span className="small muted" style={{ wordBreak: 'break-all' }}>{shared}</span> : null}
                <div className="gqr" role="img" aria-label="The group order's link as a QR code"><Svg html={qrSvg(g.link)} /></div>
                <span className="small muted" style={{ textAlign: 'center' }}>Or let them scan this.</span>
              </section>
              <div className="gfoot">
                {unavailable && <p className="err" role="alert" style={{ margin: 0 }}>Something in the list isn't available today. Take it off to pay.</p>}
                <button type="button" className="yellow" disabled={busy || !g.payable || !g.lines.length || unavailable || !orderingOpen(s)} onClick={pay}>{busy ? 'One moment…' : `Pay ${fmt(g.totalPence)} for everyone`}</button>
                {live ? <button type="button" className="btn2" style={{ minHeight: 44 }} disabled={busy} onClick={() => run(() => groupApi.act(token, 'lock'), 'Locked: nobody can add or change anything now')}>Lock the group</button> : <button type="button" className="btn2" style={{ minHeight: 44 }} disabled={busy} onClick={() => run(() => groupApi.act(token, 'unlock'), 'Open again for adding')}>Unlock for more adding</button>}
                <span className="small muted" style={{ textAlign: 'center' }}>Paying locks the group. You pay as usual: card, store credit, rewards and codes. Friends who are signed in earn points on their own items; you earn the rest.</span>
                <button type="button" className="ghost" disabled={busy} onClick={() => window.confirm('Cancel this group order? Everything added is dropped.') && run(() => groupApi.act(token, 'cancel'), 'Group order cancelled')}>Cancel this group order</button>
              </div>
            </>
          ) : (
            <div className="dbl"><Svg html={IC.people} /><span className="grow">{live ? <>Nothing to pay here: <b>{g.hostName}</b> pays and collects. {mine.length ? 'You can change your items until the group is locked.' : ''}{s.me ? ' Your items earn you points.' : ''}</> : <>Locked by {g.hostName}. {mine.length ? 'Your items are in.' : "You didn't add anything."}</>}</span></div>
          )}
        </>
      )}
      {g.state === 'cancelled' && !g.order && <div className="card col" style={{ padding: 18, gap: 6 }}><b>This group order was cancelled</b><span className="small muted">{isHost ? 'Start another from the Order tab whenever you like.' : `Ask ${g.hostName} if lunch is still on.`}</span></div>}
    </Scroll>
  );
}
